import { geoapifyApiKey, geoapifyEnabled } from 'src/config'

const AUTO_COMPLETE_BASE_URL = 'https://api.geoapify.com/v1/geocode/autocomplete'

/**
 * @param {import('@quasar/app-webpack').BootFileParams} params
 */
export default async ({ app }) => {
  if (!geoapifyEnabled) return

  function getUnitSystem () {
    const lang = navigator.language || navigator.userLanguage
    if (lang.startsWith('en-US') || lang.startsWith('en-GB')) {
      return 'mi'
    } else {
      return 'km'
    }
  }

  function getAutocompleteOptions (address, countryCodes = []) {
    if (_.isEmpty(address)) return []

    const endpointParts = [`${AUTO_COMPLETE_BASE_URL}?apiKey=${geoapifyApiKey}`]
    endpointParts.push(`text=${address}`)
    if (countryCodes.length > 0) {
      endpointParts.push(`&filter=countrycode:${_.map(countryCodes, _.toLower).join(',')}`)
    }

    const endpoint = endpointParts.join('&')

    return fetch(endpoint)
      .then(response => response.json())
      .then(data => {
        return data.features
      })
      .catch(error => {
        if (typeof Rollbar !== 'undefined') {
          // eslint-disable-next-line no-undef
          Rollbar.error('Error fetching autocomplete options', error)
        } else {
          console.error('Error fetching autocomplete options', error)
        }
        return []
      })
  }

  function getCoordinatesFromAutocomplete (autocomplete) {
    return {
      lat: autocomplete.geometry.coordinates[0],
      lon: autocomplete.geometry.coordinates[1]
    }
  }

  function getClosestLocations (origin, locations) {
    if (!origin || !locations || !locations.length) return null

    const targetLat = origin.lat
    const targetLon = origin.lon

    if (!targetLat || !targetLon) return null

    const unitSystem = getUnitSystem()

    // Haversine formula to calculate distance between two points on Earth
    function calculateDistance (lat1, lon1, lat2, lon2) {
      const R = 6371 // Earth's radius in kilometers
      const dLat = toRad(lat2 - lat1)
      const dLon = toRad(lon2 - lon1)
      const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) *
        Math.sin(dLon / 2) * Math.sin(dLon / 2)
      const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
      const km = R * c
      const miles = km * 0.621371 // Convert kilometers to miles
      return {
        km: Math.round(km * 10) / 10, // Round to 1 decimal place
        miles: Math.round(miles * 10) / 10 // Round to 1 decimal place
      }
    }

    function toRad (degrees) {
      return degrees * (Math.PI / 180)
    }

    const locationsWithDistance = []
    // Calculate distance for each location and sort by distance
    locations.forEach(location => {
      if (!location.lat || !location.lon) return null
      const distance = calculateDistance(
        targetLat,
        targetLon,
        location.lat,
        location.lon
      )

      // Set the distance values based on the unit system
      const distanceValue = unitSystem === 'mi' ? distance.miles : distance.km
      const distanceText = `${distanceValue} ${unitSystem}`

      // Use setters for the distance properties
      location.currentDistanceValue = distanceValue
      location.currentDistanceText = distanceText

      locationsWithDistance.push(location)
    })

    // Sort locations by distance (closest first)
    return locationsWithDistance.sort((a, b) => a.currentDistanceValue - b.currentDistanceValue)
  }

  app.config.globalProperties.$geoapify = {
    getClosestLocations,
    getAutocompleteOptions,
    getCoordinatesFromAutocomplete
  }
}
